import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import type { CorrectVehicleInput, VehicleCorrectionField, VehicleDetail } from 'used-vehicle-contracts';
import { orpc } from '../../../apis/orpc-query';
import { Button } from '../../../components/ui/Button';
import { Dialog } from '../../../components/ui/Dialog';
import { FormField } from '../../../components/ui/FormField';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Textarea } from '../../../components/ui/Textarea';
import { formatDay, formatMoney, formatText } from '../../../lib/format';
import { refusalReasons, serverMessage, submitErrorKey } from '../../../lib/form-helpers';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';

export interface CorrectionDialogProps {
  vehicle: VehicleDetail;
  isOpen: boolean;
  onClose: () => void;
  onChanged: () => Promise<void>;
}

/** What each field is typed as, so the input asks for the right thing rather than for "a value". */
type FieldKind = 'money' | 'date' | 'text' | 'contact' | 'costItem';

const FIELD_KINDS: Record<VehicleCorrectionField, FieldKind> = {
  purchasePrice: 'money',
  purchaseDate: 'date',
  salePrice: 'money',
  saleDate: 'date',
  buyerContactId: 'contact',
  sellerContactId: 'contact',
  askingPrice: 'money',
  storageLocation: 'text',
  costItemActual: 'costItem',
};

/** The purchase facts are closed from the moment the vehicle exists; the rest need a completed sale. */
const OPEN_VEHICLE_FIELDS: readonly VehicleCorrectionField[] = ['purchasePrice', 'purchaseDate', 'sellerContactId'];

/**
 * "Correct…" (plan §5.6; PP §4).
 *
 * An administrator picks one field, sees **what it holds now**, types what it should hold, and says why.
 * Nothing is edited in place: the service records the decision as a `CORRECTION` approval, the old and
 * new values as an activity row, and — for a figure — a fresh snapshot, so the dossier and the dashboard
 * move together.
 *
 * The field list is filtered by the vehicle's status rather than by taste: a sold record accepts all nine,
 * an open one accepts only the three facts of the purchase, because everything else about an open vehicle
 * still has an ordinary edit of its own.
 */
export const CorrectionDialog = ({ vehicle, isOpen, onClose, onChanged }: CorrectionDialogProps) => {
  const { t } = useI18nTranslation();
  const isSold = vehicle.status === 'SOLD';

  const fields = useMemo<VehicleCorrectionField[]>(
    () =>
      (Object.keys(FIELD_KINDS) as VehicleCorrectionField[]).filter(
        (field) => (isSold || OPEN_VEHICLE_FIELDS.includes(field)) && (field !== 'costItemActual' || isSold),
      ),
    [isSold],
  );

  const [field, setField] = useState<VehicleCorrectionField>(fields[0] ?? 'purchasePrice');
  const [value, setValue] = useState('');
  const [costItemId, setCostItemId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [reasons, setReasons] = useState<string[]>([]);

  const correct = useMutation(orpc.vehicles.correct.mutationOptions());
  const costsQuery = useQuery(
    orpc.costs.list.queryOptions({ input: { owner: { type: 'VEHICLE', id: vehicle.id } }, enabled: isOpen && field === 'costItemActual' }),
  );

  useEffect(() => {
    if (isOpen) {
      setField(fields[0] ?? 'purchasePrice');
      setValue('');
      setCostItemId('');
      setReason('');
      setError(null);
      setReasons([]);
    }
  }, [isOpen, fields]);

  const kind = FIELD_KINDS[field];
  // Only work that has started carries an actual: an `ESTIMATED` item has nothing to correct yet.
  const correctableItems = (costsQuery.data?.items ?? []).filter((item) => item.status === 'DONE' || item.status === 'IN_PROGRESS');
  const selectedItem = correctableItems.find((item) => item.id === costItemId) ?? null;

  const current = ((): string => {
    switch (field) {
      case 'purchasePrice':
        return formatMoney(vehicle.purchasePrice, { exact: true });
      case 'salePrice':
        return formatMoney(vehicle.salePrice, { exact: true });
      case 'askingPrice':
        return formatMoney(vehicle.askingPrice, { exact: true });
      case 'purchaseDate':
        return formatDay(vehicle.purchaseDate);
      case 'saleDate':
        return formatDay(vehicle.saleDate);
      case 'buyerContactId':
        return formatText(vehicle.buyer === null ? null : `${vehicle.buyer.name} · ${vehicle.buyer.phone}`);
      case 'sellerContactId':
        return formatText(vehicle.seller === null ? null : `${vehicle.seller.name} · ${vehicle.seller.phone}`);
      case 'storageLocation':
        return formatText(vehicle.storageLocation);
      case 'costItemActual':
        return selectedItem === null ? '—' : formatMoney(selectedItem.actualToDate, { exact: true });
    }
  })();

  const canSubmit = reason.trim() !== '' && value.trim() !== '' && (kind !== 'costItem' || costItemId !== '');

  const submit = async () => {
    setError(null);
    setReasons([]);

    const input: CorrectVehicleInput = {
      vehicleId: vehicle.id,
      expectedRevision: vehicle.revision,
      field,
      newValue: kind === 'money' || kind === 'costItem' ? Number(value) : value.trim(),
      ...(kind === 'costItem' ? { costItemId } : {}),
      reason: reason.trim(),
    };

    try {
      await correct.mutateAsync(input);
      await onChanged();
      onClose();
    } catch (caught: unknown) {
      setReasons(refusalReasons(caught));
      setError(serverMessage(caught) ?? t(submitErrorKey(caught)));
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('vehicles.correctionDialogTitle', { stockNo: vehicle.stockNo })}
      description={t('vehicles.correctionDialogBody')}
      footer={
        <>
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            isLoading={correct.isPending}
            disabled={!canSubmit}
            onClick={() => void submit()}
          >
            {t('vehicles.saveCorrection')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error === null ? null : (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        )}

        <FormField
          id="correction-field"
          label={t('vehicles.correctionField')}
        >
          <Select
            id="correction-field"
            value={field}
            onChange={(event) => {
              setField(event.target.value as VehicleCorrectionField);
              setValue('');
              setCostItemId('');
            }}
            options={fields.map((candidate) => ({ value: candidate, label: t(`vehicles.correctionFields.${candidate}`) }))}
          />
        </FormField>

        {kind === 'costItem' ? (
          <FormField
            id="correction-cost-item"
            label={t('vehicles.correctionCostItem')}
            hint={correctableItems.length === 0 ? t('vehicles.correctionNoCostItems') : undefined}
          >
            <Select
              id="correction-cost-item"
              value={costItemId}
              placeholder={t('vehicles.correctionCostItemPlaceholder')}
              onChange={(event) => setCostItemId(event.target.value)}
              options={correctableItems.map((item) => ({ value: item.id, label: `${item.name} · ${t(`enums.costStatus.${item.status}`)}` }))}
            />
          </FormField>
        ) : null}

        <div className="rounded-md border border-border bg-muted px-3 py-2 text-sm">
          <span className="text-muted-foreground">{t('vehicles.correctionCurrent')}</span>{' '}
          <span
            dir="ltr"
            className="font-medium tabular-nums"
          >
            {current}
          </span>
        </div>

        <FormField
          id="correction-value"
          label={t('vehicles.correctionNewValue')}
          hint={kind === 'contact' ? t('vehicles.correctionContactHint') : undefined}
        >
          <Input
            id="correction-value"
            type={kind === 'money' || kind === 'costItem' ? 'number' : kind === 'date' ? 'date' : 'text'}
            {...(kind === 'money' || kind === 'costItem' ? { min: 0, step: 0.001 } : {})}
            dir={kind === 'text' ? undefined : 'ltr'}
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
        </FormField>

        <FormField
          id="correction-reason"
          label={t('vehicles.correctionReason')}
          hint={t('vehicles.correctionReasonHint')}
        >
          <Textarea
            id="correction-reason"
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </FormField>

        {reasons.length === 0 ? null : (
          <ul className="list-inside list-disc space-y-1 text-xs text-muted-foreground">
            {reasons.map((blocker) => (
              <li key={blocker}>{t(`vehicles.correctionBlockers.${blocker}`, { defaultValue: blocker })}</li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
};
